/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import PageAccessLevel from '@linc-technologies/linced-common/enums/page-access-level';
import type PageType from '@linc-technologies/linced-common/enums/page-type';
import type ReactionType from '@linc-technologies/linced-common/enums/reaction-type';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import type Assessment from 'linced-admin/models/assessments/assessment';
import type Subject from 'linced-admin/models/goals/subject';
import type Person from 'linced-admin/models/person';
import type PostTemplate from 'linced-admin/models/posts/post-template';
import type TagCategory from 'linced-admin/models/posts/tag-category';
import type UsageSet from 'linced-admin/models/schools/usage-set';

// Proto: posts.PageTemplate.
export default class Page extends Model {
  declare [Type]: 'posts/page';

  // Page meta
  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof PageType> | 0;

  @attr('boolean', { defaultValue: false })
  declare isPublished: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasAssessments: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasPosts: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasGoals: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasEffort: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasGoalsAsTags: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasOTJAssessment: boolean;

  @attr('string', { defaultValue: '' })
  declare key: string;

  // Page configuration
  @attr('string', { defaultValue: '' })
  declare icon: string;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare title: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @attr('string', { defaultValue: '' })
  declare caregiverDescription: string;

  @attr('string', { defaultValue: '' })
  declare learnerDescription: string;

  @attr('number', { defaultValue: 0 })
  declare reactionType: EnumValue<typeof ReactionType> | 0;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  // Page visibility
  @attr('boolean', { defaultValue: false })
  declare visibleInMenu: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleOnContent: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleOnStudentProfile: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToParents: boolean;

  @attr('boolean', { defaultValue: false })
  declare visibleToLearners: boolean;

  // Page authorisation / permissions
  @attr('boolean', { defaultValue: false })
  declare staffCanPublish: boolean;

  @attr('boolean', { defaultValue: false })
  declare staffCanEdit: boolean;

  @attr('boolean', { defaultValue: false })
  declare caregiversCanPost: boolean;

  @attr('boolean', { defaultValue: false })
  declare learnersCanPost: boolean;

  @attr('boolean', { defaultValue: false })
  declare canComment: boolean;

  // Page content filters
  @hasMany('assessments/assessment', { async: true, inverse: null })
  declare assessmentIds: AsyncHasMany<Assessment>;

  @alias('assessmentIds')
  declare assessments: AsyncHasMany<Assessment>;

  @hasMany('goals/subject', { async: true, inverse: null })
  declare goalSubjectIds: AsyncHasMany<Subject>;

  @alias('goalSubjectIds')
  declare goalSubjects: AsyncHasMany<Subject>;

  @hasMany('posts/tag-category', { async: true, inverse: null })
  declare tagCategoryIds: AsyncHasMany<TagCategory>;

  @alias('tagCategoryIds')
  declare tagCategories: AsyncHasMany<TagCategory>;

  // Post template
  @attr('string', { defaultValue: '' })
  declare postTemplate: string;

  @hasMany('posts/post-template', { async: true, inverse: null })
  declare staffTemplateIds: AsyncHasMany<PostTemplate>;

  @hasMany('posts/post-template', { async: true, inverse: null })
  declare learnerTemplateIds: AsyncHasMany<PostTemplate>;

  @hasMany('posts/post-template', { async: true, inverse: null })
  declare caregiverTemplateIds: AsyncHasMany<PostTemplate>;

  @attr('boolean', { defaultValue: false })
  declare staffRequireTemplate: boolean;

  @attr('boolean', { defaultValue: false })
  declare learnersRequireTemplate: boolean;

  @attr('boolean', { defaultValue: false })
  declare caregiversRequireTemplate: boolean;

  // Page magic
  @attr('boolean', { defaultValue: false })
  declare hasLearnerIcon: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasAutogroup: boolean;

  // Access level for page
  @attr('number', { defaultValue: PageAccessLevel.STANDARD })
  declare accessLevel: EnumValue<typeof PageAccessLevel> | 0;

  // Is this a Sickbay page
  @attr('boolean', { defaultValue: false })
  declare isSickbay: boolean;

  @attr('boolean', { defaultValue: false })
  declare isSuspensionPosts: boolean;

  @attr('boolean', { defaultValue: false })
  declare isSuspensionLetters: boolean;

  @attr('boolean', { defaultValue: false })
  declare isBehaviour: boolean;

  @attr('boolean', { defaultValue: false })
  declare isAttendance: boolean;

  // Usage sets membership
  @hasMany('schools/usage-set', { async: true, inverse: 'pageIds' })
  declare usageSetIds: AsyncHasMany<UsageSet>;

  @computed('usageSetIds.content.[]')
  get usageSetsArray() {
    return resolved(this.usageSetIds);
  }
}
